import React, { useState, useEffect } from "react";
import Select from "react-select";
import api from "../../api/axios";

function LedgerForm({ ledger = null, onSuccess = () => {} }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [parentGroupId, setParentGroupId] = useState("");
  const [description, setDescription] = useState("");
  const [segmentAct, setSegmentAct] = useState("0");
  const [active, setActive] = useState("1");

  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [segments, setSegments] = useState([]);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const token = localStorage.getItem("accessToken");
  const branch_id = sessionStorage.getItem("selectedContext")
        ? JSON.parse(sessionStorage.getItem("selectedContext")).branch
        : 0;

  /* -------------------- Fetch parent groups -------------------- */
  useEffect(() => {
    const fetchGroups = async () => {
      setLoadingGroups(true);
      try {
        const res = await api.get("/all-group-list?per_page=100", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGroups(res.data?.data || []);
      } catch (err) {
        console.error("Error fetching groups:", err);
        setError("Failed to load groups.");
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchGroups();
  }, []);

  /* -------------------- Fetch segments (Dynamic) -------------------- */
  useEffect(() => {
    const fetchSegments = async () => {
      setLoadingSegments(true);
      try {
        const res = await api.get("/load-segments", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.data?.data || [];
        const formatted = data.map((seg) => ({
          value: seg.id,
          label: `${seg.ledger_code} - ${seg.ledger_name}`,
        }));
        setSegments(formatted);

        // ✅ If editing, match already-saved segment IDs
        if (ledger?.segments) {
          try {
            const parsed = JSON.parse(ledger.segments);
            const ids = Object.values(parsed).map((v) => parseInt(v));
            const matched = formatted.filter((opt) => ids.includes(opt.value));
            setSelectedSegments(matched);
          } catch (e) {
            console.error("Error parsing ledger.segments:", e);
            setSelectedSegments([]);
          }
        }
      } catch (err) {
        console.error("Error loading segments:", err);
        setError("Failed to load segments.");
      } finally {
        setLoadingSegments(false);
      }
    };
    fetchSegments();
  }, [ledger]);

  /* -------------------- Handle segment selection -------------------- */
  const handleSegmentChange = (selected) => {
    setSelectedSegments(selected || []);
  };

  /* -------------------- Handle form submit -------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!name.trim()) return setError("Account name is required.");
    if (!code.trim()) return setError("Account code is required.");
    if (!parentGroupId) return setError("Parent Group / Head is required.");

    setSaving(true);
    try {
      const payload = {
        branch_id: branch_id,
        ledger_name: name.trim(),
        ledger_code: code.trim(),
        description: description || null,
        group_id: parseInt(parentGroupId) || null,
        segment_capability: parseInt(segmentAct),
        active: parseInt(active) || 0,
        segments: selectedSegments.map((s) => s.value), // ✅ IDs only
      };

      if (ledger && ledger.id) {
        await api.put(`/ledgers/${ledger.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess("Account updated successfully.");
      } else {
        await api.post("/ledgers", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess("Account created successfully.");
      }

      onSuccess && onSuccess();
    } catch (err) {
      console.error("Save error:", err);
      const msg =
        err.response?.data?.message ||
        err.response?.data?.errors?.[0] ||
        "Failed to save ledger.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  /* -------------------- Handle edit mode / reset -------------------- */
  useEffect(() => {
    if (ledger) {
      setName(ledger.ledger_name ?? "");
      setCode(ledger.ledger_code ?? "");
      setParentGroupId(ledger.group_id ? String(ledger.group_id) : "");
      setDescription(ledger.description ?? "");
      setSegmentAct(String(ledger.segment_capability ?? 0));
      setActive(String(ledger.active ?? 1));

      // ✅ Handle pre-selected segments
      if (ledger.segments) {
        let parsedSegments = [];

        try {
          parsedSegments = JSON.parse(ledger.segments);
          if (typeof parsedSegments === "object") {
            parsedSegments = Object.values(parsedSegments);
          }
        } catch (e) {
          parsedSegments = [];
        }

        const preSelected = segments.filter((seg) =>
          parsedSegments.includes(seg.value)
        );
        setSelectedSegments(preSelected);
      } else {
        // ✅ Clear if no segments in DB
        setSelectedSegments([]);
      }
    } else {
      // ✅ Reset for new form
      setName("");
      setCode("");
      setParentGroupId("");
      setDescription("");
      setSegmentAct("0");
      setActive("1");
      setSelectedSegments([]); // clear when adding new
    }

    setError("");
    setSuccess("");
  }, [ledger, segments]);

  const handleReset = () => {
    if (ledger) {
      setName(ledger.ledger_name ?? "");
      setCode(ledger.ledger_code ?? "");
      setParentGroupId(ledger.group_id ?? "");
      setDescription(ledger.description ?? "");
      setSegmentAct(String(ledger.segment_capability ?? 0));
      setActive(ledger.active ?? "1");
    } else {
      setName("");
      setCode("");
      setParentGroupId("");
      setDescription("");
      setSegmentAct("0");
      setActive("1");
      setSelectedSegments([]);
    }
    setError("");
    setSuccess("");
  };

  /* -------------------- Render form -------------------- */
  return (
    <div>
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={handleSubmit} onReset={handleReset}>
        <div className="row mb-3 ledger-table">
          <div className="col-md-6">
            <label className="form-label fw-bold">Account Name*</label>
            <input
              type="text"
              name="ledger_name"
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-bold">Account Code*</label>
            <input
              type="text"
              name="ledger_code"
              className="form-control"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="row mb-3 ledger-table">
          <div className="col-md-6">
            <label className="form-label fw-bold">Parent Head / Group*</label>
            <select
              name="group_id"
              className="form-select"
              value={parentGroupId}
              onChange={(e) => setParentGroupId(e.target.value)}
            >
              <option value="">Choose...</option>
              {loadingGroups && <option disabled>Loading...</option>}
              {groups.map((g) => {
                const label = g.name ?? g.group_name ?? `#${g.id}`;
                return (
                  <option key={g.id} value={g.id}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>

          {/* ✅ Dynamic Segments dropdown */}
          <div className="col-md-6">
            <label className="form-label fw-bold">
              Connect Segments (if any)
            </label>
            <Select
              isMulti
              options={segments}
              value={selectedSegments}
              onChange={handleSegmentChange}
              isLoading={loadingSegments}
              placeholder={
                loadingSegments ? "Loading segments..." : "Select segments"
              }
              classNamePrefix="react-select"
            />
          </div>
        </div>

        <div className="row mb-3 ledger-table">
          <div className="col-md-6">
            <label className="form-label fw-bold">Act as Segment</label>
            <select
              name="segment_capability"
              className="form-select"
              value={segmentAct}
              onChange={(e) => setSegmentAct(e.target.value)}
            >
              <option value="0">No</option>
              <option value="1">Yes</option>
            </select>
          </div>

          <div className="col-md-6">
            <label className="form-label fw-bold">Status</label>
            <select
              name="active"
              className="form-select"
              value={active}
              onChange={(e) => setActive(e.target.value)}
            >
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>
        </div>

        <div className="row mb-3 ledger-table">
          <div className="col-md-6">
            <label className="form-label fw-bold">Description</label>
            <textarea
              name="description"
              className="form-control"
              rows="3"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>

        <div className="d-grid gap-2 d-md-flex justify-content-md-end">
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving
              ? ledger
                ? "Updating..."
                : "Saving..."
              : ledger
              ? "Update"
              : "Save"}
          </button>
          <button className="btn btn-secondary" type="reset" onClick={handleReset}>
            Reset
          </button>
        </div>
      </form>
    </div>
  );
}

export default LedgerForm;